import { getRpcCurrentFleetId } from "./generated/calls/rpc-current-fleet-id/rpc-current-fleet-id";
import { getRpcLatestLocationForVehicle } from "./generated/calls/rpc-latest-location-for-vehicle/rpc-latest-location-for-vehicle";
import { getRpcLatestLocationsForFleet } from "./generated/calls/rpc-latest-locations-for-fleet/rpc-latest-locations-for-fleet";
import { getRpcVehiclesForLocation } from "./generated/calls/rpc-vehicles-for-location/rpc-vehicles-for-location";
import { getVehicleImages } from "./generated/calls/vehicle-images/vehicle-images";
import { getVehicles, patchVehicles } from "./generated/calls/vehicles/vehicles";
import type {
  Vehicles as VehicleDTO,
  VehicleImages as VehicleImageDTO,
  VehicleLocations as VehicleLocationDTO,
  VehiclesUpdate as VehicleUpdateDTO,
} from "./generated/models";
import { buildHeaders, headersToObject } from "./helpers/headerHelper";
import { postgrestError } from "./helpers/postgrestErrorHelper";
import { postgrestFilter } from "./helpers/queryHelper";
import type { APIResultNoContent, APIResult } from "./types";

/**
 * Vehicle data enriched with its associated images.
 *
 * Image data is guaranteed to be an array, but may be empty when a vehicle
 * has no images or image enrichment is unavailable.
 */
type VehicleExtendedDTO = VehicleDTO & {
  vehicleImagesData: VehicleImageDTO[];
};

/**
 * Fetches the fleet vehicles and enriches each vehicle with its associated
 * images.
 *
 * Vehicle images are supplementary data, so failure to fetch them does not
 * cause the vehicle fetch to fail. Vehicles are returned with an empty
 * `vehicleImagesData` array when image enrichment is unavailable.
 */
async function fetchFleetVehicles(): Promise<APIResult<VehicleExtendedDTO[]>> {
  try {
    const headers = buildHeaders();
    const vehicleFetchRes = await getVehicles(undefined, { headers });

    let vehicleData: VehicleExtendedDTO[];

    switch (vehicleFetchRes.status) {
      // A read narrowed by `limit`, `offset` or a `Range` header answers 206
      // and carries the same rows as a 200.
      case 200:
      case 206:
        vehicleData = vehicleFetchRes.data.map<VehicleExtendedDTO>((v) => ({
          ...v,
          vehicleImagesData: [],
        }));
        break;
      default:
        throw postgrestError("Fetch failed", vehicleFetchRes);
    }

    try {
      const vehicleImageRes = await getVehicleImages(undefined, { headers });

      if (vehicleImageRes.status === 200) {
        vehicleImageRes.data.forEach((i) => {
          const vehicle = vehicleData.find((v) => v.id === i.vehicle_id);

          vehicle?.vehicleImagesData.push(i);
        });
      }
    } catch {
      // Image data is supplementary; an image fetch failure must not fail the
      // vehicle fetch.
    }

    return {
      success: true,
      data: vehicleData,
    };
  } catch (error) {
    return {
      success: false,
      errorMsg:
        error instanceof Error ? error.message : "an unknown error occurred",
    };
  }
}

/**
 * Fetches the latest known location for a single vehicle.
 *
 * `vehicleExternalId` is the vehicle's external id (`vehicles.id`, matching
 * `Vehicle.externalId`), not a local/domain id.
 *
 * @returns The vehicle's latest location, or `null` when none has been
 *          recorded yet.
 */
async function getVehicleLocation(
  vehicleExternalId: string,
): Promise<APIResult<VehicleLocationDTO | null>> {
  try {
    const headers = buildHeaders();
    const res = await getRpcLatestLocationForVehicle(
      { p_vehicle_id: vehicleExternalId },
      { headers },
    );

    if (res.status !== 200) {
      throw postgrestError("Fetch failed", res);
    }

    return {
      success: true,
      data: res.data ?? null,
    };
  } catch (error) {
    return {
      success: false,
      errorMsg:
        error instanceof Error ? error.message : "an unknown error occurred",
    };
  }
}

/**
 * Fetches the latest known location for every vehicle in the current fleet.
 *
 * `latest_locations_for_fleet()` takes a fleet id rather than inferring it
 * from the caller like a table read does. Pass `fleetId` when the caller
 * already has one (e.g. `fleetStore.fleetId`) to skip resolving it; omit it
 * to have it resolved from the authenticated user's context.
 */
async function getAllVehiclesLocations(
  fleetId?: string,
): Promise<APIResult<VehicleLocationDTO[]>> {
  try {
    const headers = buildHeaders();

    let resolvedFleetId = fleetId;

    if (!resolvedFleetId) {
      const fleetIdRes = await getRpcCurrentFleetId({ headers });

      if (fleetIdRes.status !== 200) {
        throw postgrestError("Resolving the current fleet failed", fleetIdRes);
      }

      resolvedFleetId = fleetIdRes.data;
    }

    const locationsRes = await getRpcLatestLocationsForFleet(
      { p_fleet_id: resolvedFleetId },
      { headers },
    );

    if (locationsRes.status !== 200) {
      throw postgrestError("Fetch failed", locationsRes);
    }

    return {
      success: true,
      data: locationsRes.data,
    };
  } catch (error) {
    return {
      success: false,
      errorMsg:
        error instanceof Error ? error.message : "an unknown error occurred",
    };
  }
}

/**
 * Fetches the vehicles currently assigned to a location, straight from the
 * backend (`vehicles_for_location()`) rather than filtering already-fetched
 * fleet vehicles - the authoritative answer to "who's assigned here right
 * now", in case another user has changed a vehicle's home location since
 * this fleet's vehicles were last fetched.
 *
 * `locationExternalId` is the location's external id (`locations.id`,
 * matching `SavedLocation.externalId`), not a local/domain id.
 */
async function getVehiclesForLocation(
  locationExternalId: string,
): Promise<APIResult<VehicleDTO[]>> {
  try {
    const headers = buildHeaders();
    const res = await getRpcVehiclesForLocation(
      { p_location_id: locationExternalId },
      { headers },
    );

    if (res.status !== 200) {
      throw postgrestError("Fetch failed", res);
    }

    return {
      success: true,
      data: res.data,
    };
  } catch (error) {
    return {
      success: false,
      errorMsg:
        error instanceof Error ? error.message : "an unknown error occurred",
    };
  }
}

/**
 * Updates a single vehicle's row.
 *
 * `vehicleId` is the vehicle's external id (`vehicles.id`, matching
 * `Vehicle.externalId`), not a local/domain id.
 */
async function updateVehicle(
  vehicleId: string,
  vehicleData: VehicleUpdateDTO,
): Promise<APIResultNoContent> {
  try {
    const headers = buildHeaders();
    const updateVehicleRes = await patchVehicles(
      vehicleData,
      { id: postgrestFilter(vehicleId) },
      { headers: headersToObject(headers) },
    );

    switch (updateVehicleRes.status) {
      case 200:
        return {
          success: true,
        };

      default:
        throw postgrestError("Update failed", updateVehicleRes);
    }
  } catch (error) {
    return {
      success: false,
      errorMsg:
        error instanceof Error ? error.message : "an unknown error occurred",
    };
  }
}

export {
  fetchFleetVehicles,
  getAllVehiclesLocations,
  getVehicleLocation,
  getVehiclesForLocation,
  updateVehicle,
};
export type {
  VehicleDTO,
  VehicleExtendedDTO,
  VehicleImageDTO,
  VehicleLocationDTO,
  VehicleUpdateDTO,
};

